import React from "react";
import { Modal } from "@/components";
import styles from "./TournamentLevelInfo.module.scss";

interface TournamentLevelInfoProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  level?: string;
}

const LEVEL_DESCRIPTIONS: Record<string, { title: string; description: string }> = {
  "1.0": {
    title: "Уровень 1.0 - Начинающий",
    description:
      "Вы только начинаете играть в падел. Знакомитесь с правилами игры, учитесь базовой технике ударов и передвижению по корту. Игровой опыт минимальный или отсутствует.",
  },
  "1.5": {
    title: "Уровень 1.5 - Начинающий+",
    description:
      "Вы уже знакомы с основными правилами падела, можете выполнять базовые удары. Начинаете понимать тактику игры, но еще нуждаетесь в практике.",
  },
  "2.0": {
    title: "Уровень 2.0 - Любитель",
    description:
      "Вы уверенно играете в падел, знаете правила и основные тактические приемы. Можете поддерживать розыгрыш, выполняете большинство базовых ударов стабильно.",
  },
  "2.5": {
    title: "Уровень 2.5 - Любитель+",
    description:
      "Вы имеете хороший игровой опыт, стабильно выполняете различные удары. Начинаете использовать продвинутые тактические элементы игры.",
  },
  "3.0": {
    title: "Уровень 3.0 - Средний",
    description:
      "Вы хорошо знаете правила и тактику падела. Имеете достаточный опыт для участия в турнирах. Можете эффективно играть в паре, понимаете позиционирование на корте.",
  },
  "3.5": {
    title: "Уровень 3.5 - Средний+",
    description:
      "Вы опытный игрок с хорошей техникой и тактикой. Уверенно играете в различных ситуациях, можете адаптироваться к стилю игры соперников.",
  },
  "4.0": {
    title: "Уровень 4.0 - Продвинутый",
    description:
      "Вы давно играете в падел, имеете отличную технику и тактическое мышление. Можете выполнять сложные удары, эффективно работаете в паре.",
  },
  "4.5": {
    title: "Уровень 4.5 - Продвинутый+",
    description:
      "Вы высококвалифицированный игрок с отличной техникой всех ударов. Понимаете тонкости тактики, можете играть на высоком уровне в турнирах.",
  },
  "5.0": {
    title: "Уровень 5.0 - Высокий",
    description:
      "Вы профессиональный уровень игрока. Отличная техника, тактика и физическая подготовка. Можете соревноваться на высоком уровне.",
  },
  "5.5": {
    title: "Уровень 5.5 - Высокий+",
    description:
      "Вы играете на очень высоком уровне. Превосходная техника, тактическое мышление и физическая форма. Опыт участия в серьезных турнирах.",
  },
  "6.0": {
    title: "Уровень 6.0 - Эксперт",
    description:
      "Вы эксперт в паделе. Выдающаяся техника, тактика и физическая подготовка. Можете играть на профессиональном уровне.",
  },
  "6.5": {
    title: "Уровень 6.5 - Эксперт+",
    description:
      "Вы играете на экспертном уровне. Превосходная техника всех элементов игры, глубокое понимание тактики, отличная физическая форма.",
  },
  "7.0": {
    title: "Уровень 7.0 - Мастер",
    description:
      "Вы мастер падела. Высший уровень техники, тактики и физической подготовки. Можете соревноваться на профессиональном уровне в любых турнирах.",
  },
};

export const TournamentLevelInfo: React.FC<TournamentLevelInfoProps> = ({
  isOpen,
  setIsOpen,
  level,
}) => {
  const description = level && LEVEL_DESCRIPTIONS[level]
    ? LEVEL_DESCRIPTIONS[level]
    : {
        title: "Информация об уровнях турнира",
        description:
          "Уровни в турнирах по паделу определяются от 1.0 (начинающий) до 7.0 (мастер). Каждый уровень отражает ваши навыки игры, технику, тактику и опыт участия в турнирах.",
      };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Modal
      title={description.title}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      closeModal={handleClose}
      modalWindowWidth={500}
    >
      <div className={styles.content}>
        <p className={styles.description}>{description.description}</p>
        {!level && (
          <div className={styles.levelsList}>
            <h3 className={styles.levelsTitle}>Уровни турнира:</h3>
            <ul className={styles.levels}>
              {Object.keys(LEVEL_DESCRIPTIONS).map((levelKey) => (
                <li key={levelKey} className={styles.levelItem}>
                  <span className={styles.levelValue}>{levelKey}</span>
                  <span className={styles.levelName}>
                    {LEVEL_DESCRIPTIONS[levelKey].title.split(" - ")[1]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
